// RAL Color Database
// Curated list of the only RAL colors offered for interior/exterior selection.
// Each entry: id (stable), ral code, number (internal article no.), name, hex, category.
// Note: hex values are approximate representations of the official RAL specifications.

export const ralColors = [
  { id: 'ral-1015', ral: '1015', number: '075', name: 'Light Ivory', hex: '#E6D2B5', category: 'beige' },
  { id: 'ral-3005', ral: '3005', number: '071', name: 'Wine Red', hex: '#5E2129', category: 'red' },
  { id: 'ral-3011', ral: '3011', number: '087', name: 'Red', hex: '#781F19', category: 'red' },
  { id: 'ral-5002', ral: '5002', number: '098', name: 'Ultramarine Blue', hex: '#20214F', category: 'blue' },
  { id: 'ral-5004', ral: '5004', number: '051', name: 'Monument Blue', hex: '#1A1A2A', category: 'blue' },
  { id: 'ral-5011', ral: '5011', number: '065', name: 'Steel Blue', hex: '#1E2A40', category: 'blue' },
  { id: 'ral-5013', ral: '5013', number: '074', name: 'Cobalt Blue', hex: '#1E314F', category: 'blue' },
  { id: 'ral-5007', ral: '5007', number: '070', name: 'Brilliant Blue', hex: '#3B5C8C', category: 'blue' },
  { id: 'ral-6009', ral: '6009', number: '089', name: 'Fir Green', hex: '#27352A', category: 'green' },
  { id: 'ral-6005', ral: '6005', number: '064', name: 'Moss Green', hex: '#114232', category: 'green' },
  { id: 'ral-6064', ral: '6064', number: '096', name: 'Monument Green', hex: '#2C3A30', category: 'green' },
  { id: 'ral-7001', ral: '7001', number: '062', name: 'Silver Grey', hex: '#8F999F', category: 'grey' },
  { id: 'ral-7004', ral: '7004', number: '054', name: 'Signal Grey', hex: '#969992', category: 'grey' },
  { id: 'ral-7012', ral: '7012', number: '053', name: 'Basalt Grey', hex: '#4E5754', category: 'grey' },
  { id: 'ral-7015', ral: '7015', number: '093', name: 'Slate Grey', hex: '#434B4D', category: 'grey' },
  { id: 'ral-7016', ral: '7016', number: '067', name: 'Anthracite', hex: '#293133', category: 'grey' },
  { id: 'ral-7016-267', ral: '7016', number: '267', name: 'Smooth Anthracite', hex: '#293133', category: 'grey' },
  { id: 'ral-7021', ral: '7021', number: '049', name: 'Black Grey', hex: '#23282B', category: 'grey' },
  { id: 'ral-7030', ral: '7030', number: '046', name: 'Stone Grey', hex: '#8C8C7C', category: 'grey' },
  { id: 'ral-7035', ral: '7035', number: '080', name: 'Light Grey', hex: '#D4D7D2', category: 'grey' },
  { id: 'ral-7039', ral: '7039', number: '095', name: 'Quartz Grey', hex: '#6B695E', category: 'grey' },
  { id: 'ral-7044', ral: '7044', number: '055', name: 'Silk Grey', hex: '#B5B0A1', category: 'grey' },
  { id: 'ral-8008', ral: '8008', number: '020', name: 'Golden Oak', hex: '#6F4F28', category: 'brown' },
  { id: 'ral-8022', ral: '8022', number: '059', name: 'Black Brown', hex: '#1A1718', category: 'brown' },
  { id: 'ral-9001', ral: '9001', number: '066', name: 'Cream', hex: '#EDE6D6', category: 'beige' },
  { id: 'ral-9005', ral: '9005', number: '797', name: 'Black Woodgrain Ligna', hex: '#0A0A0A', category: 'black' },
  { id: 'ral-9010', ral: '9010', number: '063', name: 'White', hex: '#FFFFFF', category: 'white' },
  { id: 'ral-9016', ral: '9016', number: '064', name: 'White Smooth', hex: '#F6F6F6', category: 'white' },
];

/** Default interior white (RAL 9016 glad) — free of charge in pricing. */
export const DEFAULT_INTERIOR_WHITE_ID = 'ral-9016';

export const isStandardInteriorWhite = (colorId) => {
  if (!colorId) return true;
  if (colorId === 'white' || colorId === 'white-smooth' || colorId === 'ral-9010' || colorId === DEFAULT_INTERIOR_WHITE_ID) {
    return true;
  }
  const color = getColorById(colorId);
  return color?.ral === '9016';
};

// Helper function to get color by RAL code (returns the first match for that code)
export const getColorByRAL = (ralCode) => {
  return ralColors.find(color => color.ral === String(ralCode).padStart(4, '0'));
};

// Helper function to get color by ID (for backward compatibility)
export const getColorById = (id) => {
  if (!id) return null;

  // Direct match against the stable id (e.g. 'ral-7016', 'ral-7016-267')
  const directMatch = ralColors.find(color => color.id === id);
  if (directMatch) return directMatch;

  // Handle 'ral-XXXX' format from ColorPicker
  if (String(id).startsWith('ral-')) {
    const ralCode = String(id).substring(4);
    const ralMatch = ralColors.find(color => color.ral === ralCode.padStart(4, '0'));
    if (ralMatch) return ralMatch;
  }

  // Try to find by RAL code directly (padded to 4 digits)
  const ralMatch = ralColors.find(color => color.ral === String(id).padStart(4, '0'));
  if (ralMatch) return ralMatch;

  // Then try legacy IDs (map to one of the allowed RAL colors)
  const legacyMap = {
    'anthracite': '7016',
    'black': '9005',
    'white': '9010',
    'white-smooth': '9016',
    'green': '6009',
  };

  if (legacyMap[id]) {
    return getColorByRAL(legacyMap[id]);
  }

  return null;
};

// Color categories for filtering (only categories that have colors in the list)
export const colorCategories = [
  'all',
  'white',
  'black',
  'grey',
  'brown',
  'beige',
  'red',
  'green',
  'blue',
];
